import Vue from 'vue';
import App from './App';
import Store from './store';
import { split, includes, get } from 'lodash';
import { i18n } from './i18n';
import Router from './router';
import Vuetify from 'vuetify';
import { sync } from 'vuex-router-sync';
import '@fortawesome/fontawesome-free/css/all.css';
import 'vuetify/dist/vuetify.min.css';
import './assets/scss/app.scss';
import scrollto from 'vue-scrollto';
import config from './config.js';
import { setProduct, setFavicon, isUsingKc } from '@/utils';
import { inferApiUrl } from '@/helpers/env';
import { registerSentryLogging } from '@trimoz/trimoz-vue-error-handling';
import { registerSheddingHandling } from './errors/errors';
import { Apiv2, keycloakAdapter } from '@trimoz/trimoz-api-wrapper';
import VueMask from 'v-mask';
import VueAxe from 'vue-axe';
import InputFacade from 'vue-input-facade';
import VueSkipTo from '@vue-a11y/skip-to';

Vue.use(InputFacade);
Vue.use(VueSkipTo);
Vue.use(VueMask);

sync(Store, Router);

const product = setProduct(window.location.hostname);
const theme = config[product] ? config[product] : config.clicsante;

Vue.prototype.$api = Apiv2(inferApiUrl(), true, product, Store);

Vue.use(Vuetify);

Vue.config.errorHandler = (err) => {
    if (process.env.VUE_APP_ENV === 'development') {
        console.log(err);
    }

    Store.dispatch('errorHandling/handleError', err);
};

Vue.use(scrollto);

let langMixin = {
    created: function() {
        const localeKey = 'tmz_public_lang';
        const item = localStorage.getItem(localeKey);
        const decodedUrl = decodeURIComponent(window.location.href);
        const urlObj = new URL(decodedUrl);
        const urlParams = new URLSearchParams(urlObj.search);
        const localeOnUrl = urlParams.get('locale') || '';

        if (/^(fr|en)$/.test(localeOnUrl)) {
            localStorage.setItem('tmz_public_lang', localeOnUrl);
        }
        else {
            let queryLang = (window.location.search.match(/lang=(fr|en)/) || [])[1] || null;
            if (!includes(['en', 'fr'], item) || queryLang) {
                let language = queryLang || 'fr';
                for (let i = 0; i < navigator.languages.length && queryLang === null; i++) {
                    const browserLanguage = split(navigator.languages[i], '-')[0];
                    if (includes(['en', 'fr'], browserLanguage)) {
                        language = browserLanguage;
                        break;
                    }
                }
                localStorage.setItem(localeKey, language);
            }
        }
        this.$store.dispatch('setLocale', { i18n: this.$i18n, locale: localStorage.getItem(localeKey) });
    },
};

/* Clear the warning in the console related to the v-calendar component
* ref : https://github.com/vuetifyjs/vuetify/issues/9999
**/
const ignoreWarnMessage = 'The .native modifier for v-on is only valid on components but it was used on <div>.';
Vue.config.warnHandler = function(msg, vm, trace) {
    // `trace` is the component hierarchy trace
    if (msg === ignoreWarnMessage) {
        msg = null;
        vm = null;
        trace = null;
    }
};

// Axe a11y tests
if (process.env.VUE_APP_ENV === 'development') {
    Vue.use(VueAxe, {
        auto: true,
        allowConsoleClears: false,
        clearConsoleOnUpdate: false,
    });
}

(async() => {
    let isUserAuth = false;
    let isKeycloakInitError = false;
    try {
        if (isUsingKc) {
            const shouldCheckLogin = includes(window.location.href, 'session_state') ||
                keycloakAdapter.validateCookieTs(document.cookie);

            if (shouldCheckLogin) {
                const establishmentId = get(split(window.location.pathname, '/'), '[1]', '');
                const page = get(split(window.location.pathname, '/'), '[2]', 'take-appt');
                isUserAuth = await keycloakAdapter.init({
                    onLoad: 'check-sso',
                    redirectUri: process.env.VUE_APP_KEYCLOAK_LOGIN_REDIRECT_URI + `${establishmentId}/${page}` + window.location.search,
                });
            }

            if (!isUserAuth || (keycloakAdapter.hasCookieTs(document.cookie) && !keycloakAdapter.validateCookieTs(document.cookie))) {
                keycloakAdapter.deleteAuthTsCookie();
            }
        }
    }
    catch (e) {
        isKeycloakInitError = true;
        console.warn('Keycloak instance not initiated');
        console.error(e);
        keycloakAdapter.deleteAuthTsCookie();
    }
    finally {
        new Vue({
            el: '#app',
            i18n: i18n,
            mixins: [langMixin],
            store: Store,
            router: Router,
            vuetify: new Vuetify({
                icons: {
                    iconfont: 'fa',
                },
                options: {
                    customProperties: true,
                },
                theme: {
                    light: true,
                    themes: {
                        light: {
                            primary: theme.primary,
                            secondary: theme.secondary,
                            accent: theme.accent,
                            error: theme.error,
                            info: theme.info,
                            success: theme.success,
                            warning: theme.warning,
                            anchor: '#006ad4',
                        },
                    },
                },
            }),
            mounted: () => document.dispatchEvent(new Event('x-app-rendered')),
            render: h => h(App),
            created: async function() {
                /* Associer le produit avec le domaine avec window.location.hostname */
                this.$store.commit('UPDATE_STYLE', product);
                this.$store.commit('UPDATE_THEME', theme);
                setFavicon();

                registerSentryLogging(product);
                registerSheddingHandling(this);
                if (isUserAuth) {
                    await this.$store.dispatch('kcAuth/onLoginSuccess');
                }

                if (isKeycloakInitError) {
                    keycloakAdapter.isKcInstanceError = true;
                    this.$store.commit('kcAuth/SET_IS_KC_INIT_ERROR', true);
                }
            },
        }).$mount('#app');
    }
})();
